var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { MirrorPage } from "./domtools";
import { MQTT_EVENTS, COBROWSER_PERMISSION, API_COMMAND, } from "./model";
import { Player } from "./player";
import { PlayerBuffer } from "./player/buffer";
import { log, wait } from "./utils";
import { UPDATE_PERIOD } from "./options";
import { Zstd } from "./domtools/compression/zstd";
import { Gzip } from "./domtools/compression/native";
var ReceiverState;
(function (ReceiverState) {
    ReceiverState[ReceiverState["INITIALIZING"] = 0] = "INITIALIZING";
    ReceiverState[ReceiverState["INITIALIZED"] = 1] = "INITIALIZED";
    ReceiverState[ReceiverState["TERMINATED"] = 2] = "TERMINATED";
})(ReceiverState || (ReceiverState = {}));
/**
 * handle the receiver endpoint of a cobrowsing session
 * aks for permission, render page updates, control remote
 */
export class Receiver {
    constructor(auviousClient, config, listeners = {}) {
        this.auviousClient = auviousClient;
        this.config = config;
        this.listeners = listeners;
        this.buffer = new PlayerBuffer();
        this.permissions = {
            view: false,
            control: false,
        };
        this.options = {
            scroll: {
                enabled: false,
            },
        };
        this.sessionId = null;
        this.version = 0;
        this.state = ReceiverState.INITIALIZING;
        /** next flush is lite */
        this.flushLite = false;
        /** timeout id failsafe for notifying client of termination  */
        this.terminationCountdown = -1;
        this.compressionSupport = {
            zstd: {
                compress: false,
                decompress: false,
            },
            gzip: {
                compress: false,
                decompress: false,
            },
        };
        this.api = auviousClient.apiResourceFactory("cobrowser/api");
        this.commonSubscription = this.auviousClient.events.setEventHandlers({
            onMessageReceived: (event) => {
                if (event.payload.type.startsWith("Cobrowsing")) {
                    try {
                        this.handleEvent(event.payload);
                    }
                    catch (ex) {
                        // to log on sentry
                        console.error(ex);
                    }
                }
            },
        });
    }
    handleEvent(event) {
        this.setVersion(event.version);
        switch (event.type) {
            // case MQTT_EVENTS.CREATED: {
            //   this.onSessionCreated(event.payload);
            //   break;
            // }
            case MQTT_EVENTS.VIEW_ACCEPTED: {
                this.viewAccepted(event);
                break;
            }
            case MQTT_EVENTS.CONTROL_ACCEPTED: {
                this.controlAccepted(event);
                break;
            }
            case MQTT_EVENTS.CONTROL_REVOKED: {
                this.controlRevoked(event);
                break;
            }
            case MQTT_EVENTS.VIEW_REVOKED: {
                this.viewRevoked(event);
                break;
            }
            case MQTT_EVENTS.ACCESS_DENIED: {
                this.accessDenied(event);
                break;
            }
            case MQTT_EVENTS.TERMINATED: {
                this.terminated(event);
                break;
            }
            case MQTT_EVENTS.PAGE_UPDATED: {
                this.pageUpdated(event);
                break;
            }
            case MQTT_EVENTS.OPTION_UPDATED: {
                this.optionUpdated(event);
                break;
            }
        }
    }
    /**
     * reconnect to a previous session
     * @param sessionId the cobrowser session to reconnect to
     */
    reconnect(sessionId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.renderer) {
                return;
            }
            yield this.checkCompression();
            this.sessionId = sessionId;
            const response = yield this.api.create(this.createCommand(API_COMMAND.RECONNECT), {
                urlPostfix: `session/reconnect`,
            });
            if (response.error) {
                throw new Error(response.msg);
            }
            this.setVersion(response.version);
            this.options = Object.assign(Object.assign({}, this.options), response.options);
            this.state = ReceiverState.INITIALIZED;
            yield this.createPage();
            if (!this.renderer) {
                throw new Error("Cobrowser: reconnection cancelled.");
            }
            this.renderer.users.addUser({
                id: response.owner.id,
                endpoint: response.owner.endpoint,
            }, { control: true, view: true });
            for (const id in response.remotes) {
                this.renderer.users.addUser({ id, endpoint: response.remotes[id].remoteEndpointId }, response.remotes[id].permissions);
            }
            this.permissions.view = this.renderer.users.hasPermission(this.config.userId, COBROWSER_PERMISSION.VIEW);
            this.permissions.control = this.renderer.users.hasPermission(this.config.userId, COBROWSER_PERMISSION.CONTROL);
            if (this.permissions.control) {
                this.renderer.setEvents({ input: true });
                this.renderer.cursor.suppress("mouseout", false);
                this.renderer.cursor.suppress("mouseover", false);
                this.renderer.cursor.suppress("mousedown", false);
                this.renderer.cursor.suppress("mouseup", false);
                this.renderer.cursor.suppress("click", false);
            }
        });
    }
    clearTerminationTimer() {
        if (this.terminationCountdown !== -1) {
            clearTimeout(this.terminationCountdown);
            this.terminationCountdown = -1;
        }
    }
    setTerminationTimer() {
        this.clearTerminationTimer();
        this.terminationCountdown = window.setTimeout(() => {
            this.terminationCountdown = -1;
            this.terminated();
        }, 30000);
    }
    /** create and initialize a session, prefer requestAccess for normal flow */
    createSession(target) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.checkCompression();
            const record = this.config.record;
            const response = yield this.api.create(this.createCommand(API_COMMAND.CREATE_SESSION, {
                targetId: target.userId,
                targetEndpointId: target.userEndpointId,
                isTargetLocal: false,
                record,
                compressionSupport: this.compressionSupport,
            }), {
                urlPostfix: `session`,
            });
            if (response.error) {
                throw new Error(response.msg);
            }
            this.setVersion(response.version);
            this.sessionId = response.data.sessionId;
        });
    }
    /** end the session for all */
    terminate() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.sessionId) {
                // in any case, force termination
                this.setTerminationTimer();
                const response = yield this.api.create(this.createCommand(API_COMMAND.TERMINATE), {
                    urlPostfix: "session/terminate",
                });
                if (response.error) {
                    throw new Error(response.msg);
                }
                this.setVersion(response.version);
            }
            else if (!this.isActive()) {
                this.terminated();
            }
        });
    }
    /** a fully established cobrowsing connection */
    isActive() {
        return this.state === ReceiverState.INITIALIZED || this.renderer;
    }
    cleanup() {
        var _a, _b;
        (_a = this.renderer) === null || _a === void 0 ? void 0 : _a.cleanup();
        (_b = this.commonSubscription) === null || _b === void 0 ? void 0 : _b.dispose();
        this.clearTerminationTimer();
        this.sessionId = null;
        this.listeners = {};
        this.renderer = null;
        this.player = null;
        this.buffer = new PlayerBuffer();
        this.commonSubscription = null;
        this.state = ReceiverState.TERMINATED;
    }
    /**
     * request access to page, view or control
     * @param access View or control a page
     * @param user The user whose page we want access
     */
    requestAccess(access, user) {
        return __awaiter(this, void 0, void 0, function* () {
            const isView = access === COBROWSER_PERMISSION.VIEW;
            // do not ask for permission already granted
            if ((this.permissions.control === true && !isView) ||
                (this.permissions.view && isView)) {
                return;
            }
            yield this.checkCompression();
            const record = this.config.record && isView ? this.config.record : undefined;
            const response = yield this.api.create(this.createCommand(isView ? API_COMMAND.VIEW_REQUEST : API_COMMAND.CONTROL_REQUEST, {
                targetId: user.userId,
                targetEndpointId: user.userEndpointId,
                record,
                compressionSupport: this.compressionSupport,
            }), {
                urlPostfix: `session/permissions/${isView ? "view" : "control"}/request`,
            });
            if (response.error) {
                throw new Error(response.msg);
            }
            this.setVersion(response.version);
            if (isView) {
                this.sessionId = response.data.sessionId;
            }
        });
    }
    enable(option, settings) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.renderer) {
                this.renderer.setEvents({ scroll: true });
            }
            const response = yield this.api.create(this.createCommand(API_COMMAND.OPTION_ENABLE, {
                option,
                settings,
            }), {
                urlPostfix: `session/options/enable`,
            });
            if (response.error) {
                throw new Error(response.msg);
            }
            this.setVersion(response.version);
            this.options.scroll.enabled = true;
        });
    }
    disable(option) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.api.create(this.createCommand(API_COMMAND.OPTION_DISABLE, {
                option,
            }), {
                urlPostfix: `session/options/disable`,
            });
            if (response.error) {
                throw new Error(response.msg);
            }
            if (option === "scroll") {
                if (this.renderer) {
                    this.renderer.setEvents({ scroll: false });
                }
                this.setVersion(response.version);
                this.options.scroll.enabled = false;
            }
        });
    }
    /** must render all updates */
    pageUpdated(ev) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (this.isActive()) {
                this.buffer.append(ev.payload);
                (_a = this.player) === null || _a === void 0 ? void 0 : _a.play();
            }
        });
    }
    optionUpdated(ev) {
        return __awaiter(this, void 0, void 0, function* () {
            const [option, info] = Object.entries(ev.payload)[0];
            if (this.renderer) {
                if (option === "scroll") {
                    if (info.enabled) {
                        this.renderer.setEvents({ scroll: true });
                    }
                    else {
                        this.renderer.setEvents({ scroll: false });
                    }
                }
            }
            this.options[option] = info;
            this.sendEvent("option", {
                option,
                enabled: info.enabled,
            });
        });
    }
    canView() {
        return this.permissions.view;
    }
    canControl() {
        return this.permissions.control;
    }
    /**
     * add event listener, previous one gets overwritten
     */
    on(event, listener) {
        // @ts-ignore
        this.listeners[event] = listener;
    }
    /**
     * sends event back to client's listener
     */
    sendEvent(event, data) {
        if (this.listeners[event]) {
            try {
                this.listeners[event](data);
            }
            catch (error) {
                log(error);
            }
        }
    }
    onSessionCreated(event) {
        this.sessionId = event.sessionId;
        this.sendEvent("sessionCreated", {
            timestamp: event.timestamp,
        });
    }
    viewAccepted(event) {
        return __awaiter(this, void 0, void 0, function* () {
            if (event.payload.remoteId !== this.config.userId) {
                return;
            }
            this.sessionId = event.sessionId;
            this.permissions.view = true;
            this.state = ReceiverState.INITIALIZED;
            yield this.createPage();
            if (!this.renderer) {
                throw new Error("Cobrowser: cobrowsing cancelled.");
            }
            this.renderer.users.addUser({
                id: event.userId,
                endpoint: event.userEndpointId,
            }, { control: true, view: true });
            this.sendEvent("viewAccepted", {
                timestamp: event.timestamp,
            });
        });
    }
    checkCompression() {
        return __awaiter(this, void 0, void 0, function* () {
            yield Zstd.load(false, true);
            this.compressionSupport.zstd.decompress = Zstd.canDecompress;
            this.compressionSupport.gzip.decompress = Gzip.canDecompress;
        });
    }
    createPage() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.renderer) {
                return;
            }
            this.renderer = yield MirrorPage.create({
                target: this.config.renderer,
                user: {
                    endpoint: this.config.userEndpointId,
                    id: this.config.userId,
                    metadata: this.config.userMetadata,
                },
                compressionSupport: this.compressionSupport,
            }, null, {
                resize: (event) => this.sendEvent("resize", event),
                mousemove: (event) => this.sendEvent("mousemove", event),
                navigation: (event) => this.sendEvent("navigation", event),
                filtered: (event) => this.sendEvent("filtered", event),
            });
            if (!this.renderer || !this.isActive()) {
                return;
            }
            this.player = new Player(this.renderer, this.buffer);
            this.renderer.cursor.suppress("mouseout", true);
            this.renderer.cursor.suppress("mouseover", true);
            this.renderer.cursor.suppress("mousedown", true);
            this.renderer.cursor.suppress("mouseup", true);
            this.renderer.cursor.suppress("click", true);
            this.renderer.setEvents({
                scroll: this.options.scroll.enabled,
                cursor: true,
                highlight: true,
            });
            this.schedule();
        });
    }
    schedule() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isActive()) {
                return;
            }
            const [changes] = yield this.renderer.flush(this.flushLite);
            this.flushLite = false;
            if (changes) {
                try {
                    yield this.sendUpdates(changes);
                }
                catch (_a) {
                    yield this.sendPending(changes);
                }
            }
            setTimeout(() => this.schedule(), UPDATE_PERIOD);
        });
    }
    sendPending(data) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            let tries = 1;
            while (true) {
                try {
                    yield this.sendUpdates(data);
                    return;
                }
                catch (ex) {
                    yield wait(Math.min(++tries, 10) * UPDATE_PERIOD);
                    if (!this.isActive() ||
                        (ex === null || ex === void 0 ? void 0 : ex.status) === 410 ||
                        ((_a = ex === null || ex === void 0 ? void 0 : ex.response) === null || _a === void 0 ? void 0 : _a.status) === 410) {
                        return;
                    }
                    if (tries === 20) {
                        this.flushLite = true;
                    }
                }
            }
        });
    }
    sendUpdates(ev) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.api.create(this.createCommand(API_COMMAND.PAGE_UPDATE, ev), {
                urlPostfix: "session/remote/update",
            });
            this.setVersion(response.version);
        });
    }
    controlAccepted(event) {
        if (event.payload.remoteId !== this.config.userId) {
            return;
        }
        this.renderer.cursor.suppress("mouseout", false);
        this.renderer.cursor.suppress("mouseover", false);
        this.renderer.cursor.suppress("mousedown", false);
        this.renderer.cursor.suppress("mouseup", false);
        this.renderer.cursor.suppress("click", false);
        this.permissions.control = true;
        this.renderer.setEvents({ input: true });
        this.sendEvent("controlAccepted", {
            timestamp: event.timestamp,
        });
    }
    controlRevoked(event) {
        if (event.payload.remoteId !== this.config.userId) {
            return;
        }
        this.permissions.control = false;
        this.renderer.cursor.suppress("mouseout", true);
        this.renderer.cursor.suppress("mouseover", true);
        this.renderer.cursor.suppress("mousedown", true);
        this.renderer.cursor.suppress("mouseup", true);
        this.renderer.cursor.suppress("click", true);
        this.renderer.setEvents({ input: false });
        this.sendEvent("controlRevoked", {
            timestamp: event.timestamp,
        });
    }
    viewRevoked(event) {
        if (event.payload.remoteId !== this.config.userId) {
            return;
        }
        this.permissions.view = false;
        this.terminate();
    }
    accessDenied(event) {
        if (event.payload.remoteId !== this.config.userId) {
            return;
        }
        this.permissions[event.payload.permission] = false;
        this.sendEvent("accessDenied", {
            timestamp: event.timestamp,
            permission: event.payload.permission,
        });
    }
    terminated(event) {
        if (this.state !== ReceiverState.TERMINATED) {
            this.state = ReceiverState.TERMINATED;
            this.sendEvent("terminated", null);
        }
        this.cleanup();
    }
    setVersion(version) {
        if (Number.isInteger(version)) {
            this.version = version;
        }
    }
    createCommand(type, payload) {
        return {
            type,
            sessionId: this.sessionId,
            timestamp: new Date().toISOString(),
            userId: this.config.userId,
            userEndpointId: this.config.userEndpointId,
            payload,
            version: this.version,
        };
    }
}
//# sourceMappingURL=receiver.js.map